/**
 * gbi-quick-view — product Quick View modal.
 *
 * Trigger: <button data-quick-view-trigger data-product-handle="...">
 * Fetches /products/<handle>.js, renders modal with variant picker + 3 images,
 * AJAX-adds via window.Alpine.store('cart').addItem(variantId, qty).
 *
 * All DOM built via createElement + textContent. No innerHTML writes against user data.
 */
(function () {
  'use strict';

  let overlay = null;
  let modalRefs = null;
  let lastTrigger = null;
  let selectedVariantId = null;
  let currentHandle = null;
  let currentMinQty = 1;
  let currentCaseSize = 1;
  const productCache = new Map();

  function vtNameFor(handle) {
    if (!handle) return '';
    return 'product-hero-' + String(handle).replace(/-/g, '_');
  }

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`/products/${handle}.js`)
      .then((r) => r.json())
      .catch((err) => { productCache.delete(handle); throw err; });
    productCache.set(handle, promise);
    return promise;
  }

  function elem(tag, opts) {
    const el = document.createElement(tag);
    if (!opts) return el;
    if (opts.className) el.className = opts.className;
    if (opts.text) el.textContent = opts.text;
    if (opts.attrs) Object.keys(opts.attrs).forEach((k) => el.setAttribute(k, opts.attrs[k]));
    return el;
  }

  function clear(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function injectStyles() {
    if (document.getElementById('qv-styles')) return;
    const style = document.createElement('style');
    style.id = 'qv-styles';
    style.textContent = `
      .qv-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background: rgba(26, 16, 8, 0.7);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease;
      }
      .qv-overlay.is-open {
        opacity: 1;
        pointer-events: auto;
      }
      .qv-modal {
        position: relative;
        background: #FFFFFF;
        border-radius: 24px;
        max-width: 1080px;
        width: 100%;
        max-height: 92vh;
        overflow: auto;
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
        box-shadow: 0 30px 80px -20px rgba(0,0,0,0.4);
        transform: translateY(20px) scale(0.96);
        opacity: 0;
        transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
      }
      .qv-overlay.is-open .qv-modal {
        transform: translateY(0) scale(1);
        opacity: 1;
      }
      .qv-close {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 0;
        background: rgba(0,0,0,0.06);
        color: #1A1008;
        font-size: 18px;
        cursor: pointer;
        z-index: 2;
        line-height: 1;
        display: grid;
        place-items: center;
        transition: background 0.2s ease;
      }
      .qv-close:hover { background: rgba(0,0,0,0.12); }
      .qv-images {
        background: #FAFAF8;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0;
      }
      /* Pre-sized image slot prevents layout shift when the real image lands */
      .qv-image-slot {
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;
        overflow: hidden;
        background:
          linear-gradient(110deg, #F5EFE6 0%, #FAF7F3 30%, #F5EFE6 60%) 0 0/200% 100%;
        animation: qv-shimmer 1.8s ease-in-out infinite;
      }
      .qv-image-slot.is-hero { aspect-ratio: 4 / 5; }
      .qv-image-slot.is-loaded {
        animation: none;
        background: #FAFAF8;
      }
      .qv-image-slot img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .qv-image-seed {
        filter: blur(8px);
        transform: scale(1.06);
        opacity: 0.85;
      }
      .qv-image-slot.is-loaded .qv-image-seed { opacity: 0; }
      .qv-image-real {
        z-index: 1;
        transition: opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1);
      }
      @keyframes qv-shimmer {
        0%   { background-position: 100% 0; }
        100% { background-position: -100% 0; }
      }
      .qv-body {
        padding: 48px 40px 32px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        font-family: var(--font-primary-family, "DM Sans"), system-ui, sans-serif;
      }
      /* While the modal is loading, mute the text rows into shimmer bars so
         the modal looks intentional rather than half-rendered. */
      .qv-modal.is-loading .qv-vendor,
      .qv-modal.is-loading .qv-title,
      .qv-modal.is-loading .qv-price {
        color: transparent !important;
        background:
          linear-gradient(110deg, #F5EFE6 0%, #FAF7F3 30%, #F5EFE6 60%) 0 0/200% 100%;
        animation: qv-shimmer 1.8s ease-in-out infinite;
        border-radius: 6px;
      }
      .qv-modal.is-loading .qv-vendor { min-height: 14px; width: 30%; }
      .qv-modal.is-loading .qv-title  { min-height: 28px; width: 80%; margin-bottom: 2px; }
      .qv-modal.is-loading .qv-price  { min-height: 22px; width: 22%; }
      .qv-modal.is-loading .qv-savings,
      .qv-modal.is-loading .qv-description,
      .qv-modal.is-loading .qv-options,
      .qv-modal.is-loading .qv-qty-row,
      .qv-modal.is-loading .qv-meta,
      .qv-modal.is-loading .qv-cta-row,
      .qv-modal.is-loading .qv-trust { visibility: hidden; }
      .qv-modal.is-content-ready .qv-vendor,
      .qv-modal.is-content-ready .qv-title,
      .qv-modal.is-content-ready .qv-price {
        animation: none;
      }
      .qv-vendor {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.18em;
        color: var(--color-accent, #7B2D42);
        margin: 0;
      }
      .qv-title {
        font-family: var(--font-display, "Ancizar Sans"), Georgia, serif;
        font-size: 28px;
        font-weight: 400;
        line-height: 1.18;
        color: #1A1008;
        margin: 0;
        letter-spacing: -0.3px;
      }
      .qv-price {
        font-family: var(--font-display, "Ancizar Sans"), Georgia, serif;
        font-size: 22px;
        font-weight: 500;
        color: #1A1008;
        margin: 0;
      }
      .qv-compare {
        color: #1A1008;
        opacity: 0.45;
        text-decoration: line-through;
        font-size: 16px;
      }
      /* Savings row (You save $X · 67% off) */
      .qv-savings {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: -4px 0 4px;
      }
      .qv-savings-pill {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        padding: 5px 10px;
        border-radius: 999px;
        background: rgba(91, 142, 90, 0.12);
        color: var(--color-success, #5B8E5A);
      }
      .qv-savings-pill--accent {
        background: rgba(123, 45, 66, 0.1);
        color: var(--color-accent, #7B2D42);
      }

      /* Description block with read-more */
      .qv-description {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin: 4px 0 4px;
      }
      .qv-description-text {
        font-size: 14px;
        line-height: 1.55;
        color: #1A1008;
        opacity: 0.75;
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .qv-description-text.is-expanded {
        -webkit-line-clamp: unset;
        display: block;
      }
      .qv-description-toggle {
        align-self: flex-start;
        background: transparent;
        border: 0;
        padding: 0;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.05em;
        color: var(--color-accent, #7B2D42);
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      /* Option groups (Size, Color, ...) */
      .qv-options {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin: 4px 0 4px;
      }
      .qv-option-group { display: flex; flex-direction: column; gap: 8px; }
      .qv-option-label {
        margin: 0;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #1A1008;
        opacity: 0.7;
      }
      .qv-option-pills {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .qv-option-pill {
        padding: 9px 16px;
        background: transparent;
        border: 1px solid rgba(26, 16, 8, 0.2);
        border-radius: 999px;
        cursor: pointer;
        font-family: inherit;
        font-size: 13px;
        font-weight: 500;
        color: #1A1008;
        transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
      }
      .qv-option-pill:hover { border-color: #1A1008; }
      .qv-option-pill.is-selected {
        background: #1A1008;
        color: #FAFAF8;
        border-color: #1A1008;
      }

      /* Quantity + meta row */
      .qv-qty-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 8px 0;
        border-top: 1px solid rgba(26, 16, 8, 0.08);
      }
      .qv-qty-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #1A1008;
        opacity: 0.7;
      }
      .qv-qty-control {
        display: inline-flex;
        align-items: center;
        border: 1px solid rgba(26, 16, 8, 0.18);
        border-radius: 999px;
        overflow: hidden;
      }
      .qv-qty-btn {
        width: 36px;
        height: 36px;
        background: transparent;
        border: 0;
        cursor: pointer;
        font-size: 16px;
        color: #1A1008;
        line-height: 1;
        transition: background 0.2s ease;
      }
      .qv-qty-btn:hover { background: rgba(26,16,8,0.06); }
      .qv-qty-value {
        min-width: 32px;
        text-align: center;
        font-family: inherit;
        font-size: 14px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      /* SKU + stock meta */
      .qv-meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        font-size: 12px;
      }
      .qv-sku {
        color: #1A1008;
        opacity: 0.55;
        letter-spacing: 0.05em;
      }
      .qv-stock {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      .qv-stock::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
      .qv-stock.is-in    { background: rgba(91,142,90,0.12); color: var(--color-success, #5B8E5A); }
      .qv-stock.is-low   { background: rgba(201,168,76,0.16); color: #8C7430; }
      .qv-stock.is-out   { background: rgba(123,45,66,0.12); color: var(--color-accent, #7B2D42); }

      /* Primary CTA row */
      .qv-cta-row {
        display: flex;
        gap: 10px;
        margin-top: 8px;
      }
      .qv-add {
        flex: 1;
        padding: 16px 24px;
        background: #1A1008;
        color: #FAFAF8;
        border: 0;
        border-radius: 999px;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
      }
      .qv-add:hover:not([disabled]) {
        transform: translateY(-2px);
        box-shadow: 0 16px 32px -10px rgba(0,0,0,0.3);
      }
      .qv-add[disabled] {
        background: rgba(26,16,8,0.4);
        cursor: not-allowed;
      }
      .qv-view-full {
        display: inline-flex;
        align-items: center;
        padding: 16px 22px;
        background: transparent;
        color: #1A1008;
        border: 1px solid rgba(26, 16, 8, 0.4);
        border-radius: 999px;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        text-decoration: none;
        transition: background 0.2s ease, color 0.2s ease;
        white-space: nowrap;
      }
      .qv-view-full:hover {
        background: #1A1008;
        color: #FAFAF8;
      }

      /* Trust signals */
      .qv-trust {
        list-style: none;
        padding: 16px 0 0;
        margin: 4px 0 0;
        border-top: 1px solid rgba(26, 16, 8, 0.08);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .qv-trust-item {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 12px;
        color: #1A1008;
        opacity: 0.7;
      }
      .qv-trust-icon {
        width: 22px;
        text-align: center;
        font-size: 14px;
        flex: 0 0 22px;
      }
      @media (max-width: 767px) {
        .qv-overlay { padding: 12px; }
        .qv-modal { grid-template-columns: 1fr; max-height: 95vh; }
        .qv-images {
          flex-direction: row;
          flex-wrap: wrap;
          gap: 4px;
        }
        .qv-image-slot { width: calc(50% - 2px); aspect-ratio: 1; }
        .qv-image-slot.is-hero { width: 100%; aspect-ratio: 4 / 5; }
        .qv-body { padding: 28px 20px 24px; gap: 12px; }
        .qv-title { font-size: 22px; }
        .qv-cta-row { flex-direction: column; }
        .qv-view-full { width: 100%; justify-content: center; }
      }
      @media (prefers-reduced-motion: reduce) {
        .qv-overlay, .qv-modal { transition: none; }
      }
    `;
    document.head.appendChild(style);
  }

  function buildOverlay() {
    injectStyles();
    overlay = elem('div', {
      className: 'qv-overlay',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'qv-title' },
    });
    const modal = elem('div', { className: 'qv-modal' });
    const close = elem('button', {
      className: 'qv-close',
      text: '✕',
      attrs: { type: 'button', 'aria-label': 'Close' },
    });
    const images = elem('div', { className: 'qv-images' });
    const body = elem('div', { className: 'qv-body' });
    const vendor = elem('p', { className: 'qv-vendor' });
    const title = elem('h2', { className: 'qv-title', attrs: { id: 'qv-title' } });
    const price = elem('p', { className: 'qv-price' });
    const savings = elem('p', { className: 'qv-savings' });
    const description = elem('div', { className: 'qv-description' });
    const optionGroups = elem('div', { className: 'qv-options' });
    const meta = elem('div', { className: 'qv-meta' });
    const qtyRow = elem('div', { className: 'qv-qty-row' });
    const ctaRow = elem('div', { className: 'qv-cta-row' });
    const trust = elem('ul', { className: 'qv-trust' });

    body.appendChild(vendor);
    body.appendChild(title);
    body.appendChild(price);
    body.appendChild(savings);
    body.appendChild(description);
    body.appendChild(optionGroups);
    body.appendChild(qtyRow);
    body.appendChild(meta);
    body.appendChild(ctaRow);
    body.appendChild(trust);
    modal.appendChild(close);
    modal.appendChild(images);
    modal.appendChild(body);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });
    close.addEventListener('click', closeModal);

    modalRefs = { modal, vendor, title, price, savings, description, optionGroups, meta, qtyRow, ctaRow, trust, images, close };
    return modalRefs;
  }

  function ensureOverlay() {
    return modalRefs || buildOverlay();
  }

  function formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function buildShopifyImageUrl(src, width) {
    if (!src) return src;
    // Shopify CDN URLs accept ?width=N to request a specific render size
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  function renderSkeleton(refs, hint) {
    refs.modal.classList.remove('is-content-ready');
    refs.modal.classList.add('is-loading');
    refs.vendor.textContent = hint && hint.vendor ? hint.vendor : ' ';
    refs.title.textContent = hint && hint.title ? hint.title : ' ';
    clear(refs.price);
    refs.price.appendChild(document.createTextNode(' '));
    clear(refs.savings);
    clear(refs.description);
    clear(refs.optionGroups);
    clear(refs.meta);
    clear(refs.qtyRow);
    clear(refs.ctaRow);
    clear(refs.trust);
    clear(refs.images);
    // Pre-size the image slot with a placeholder block so the modal layout
    // is correct BEFORE the high-res image arrives. Optionally seed with
    // the small thumbnail from the trigger card for instant first paint.
    const slot = document.createElement('div');
    slot.className = 'qv-image-slot is-hero';
    if (currentHandle) {
      slot.style.viewTransitionName = vtNameFor(currentHandle);
    }
    if (hint && hint.previewSrc) {
      const seed = document.createElement('img');
      seed.className = 'qv-image-seed';
      seed.src = hint.previewSrc;
      seed.alt = '';
      seed.decoding = 'async';
      seed.fetchPriority = 'high';
      slot.appendChild(seed);
    }
    refs.images.appendChild(slot);
  }

  function attachImage(slot, src, isHero) {
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.fetchPriority = isHero ? 'high' : 'low';
    img.loading = isHero ? 'eager' : 'lazy';
    img.className = 'qv-image-real';
    // The hero image gets the view-transition-name tied to the product, so
    // navigating from Quick View → PDP morphs the same pixel rectangle.
    // Transfer the name from the slot (which had it during the skeleton phase)
    // onto the real image to keep view-transition-names unique on the page.
    if (isHero && currentHandle) {
      slot.style.viewTransitionName = '';
      img.style.viewTransitionName = vtNameFor(currentHandle);
    }
    // Start opacity 0, fade in once the bitmap is decoded
    img.style.opacity = '0';
    const fadeIn = () => {
      img.style.opacity = '1';
      slot.classList.add('is-loaded');
    };
    if (img.decode) {
      img.decode().then(fadeIn).catch(fadeIn);
    } else {
      img.addEventListener('load', fadeIn, { once: true });
      img.addEventListener('error', fadeIn, { once: true });
    }
    img.src = src;
    slot.appendChild(img);
  }

  function stripHtml(html) {
    if (!html) return '';
    // DOMParser doesn't execute scripts/images so it's safe to parse untrusted HTML
    try {
      const doc = new DOMParser().parseFromString(String(html), 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (_) {
      return String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    }
  }

  function renderMeta(metaEl, product, variant) {
    clear(metaEl);
    if (!variant) return;
    if (variant.sku) {
      const sku = elem('span', { className: 'qv-sku', text: 'SKU · ' + variant.sku });
      metaEl.appendChild(sku);
    }
    const stockEl = elem('span', { className: 'qv-stock' });
    if (variant.available === false) {
      stockEl.classList.add('is-out');
      stockEl.textContent = 'Sold out';
    } else if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
      stockEl.classList.add('is-low');
      stockEl.textContent = `Only ${variant.inventory_quantity} left`;
    } else {
      stockEl.classList.add('is-in');
      stockEl.textContent = 'In stock';
    }
    metaEl.appendChild(stockEl);
  }

  function renderProduct(product) {
    const refs = ensureOverlay();
    refs.modal.classList.remove('is-loading');
    refs.modal.classList.add('is-content-ready');
    refs.vendor.textContent = product.vendor || '';
    refs.title.textContent = product.title || '';

    // ─── PRICE + SAVINGS ─────────────────────────────────────────
    clear(refs.price);
    refs.price.appendChild(document.createTextNode(formatMoney(product.price)));
    if (product.compare_at_price && product.compare_at_price > product.price) {
      const compare = elem('span', {
        className: 'qv-compare',
        text: ' ' + formatMoney(product.compare_at_price),
      });
      refs.price.appendChild(compare);
    }

    clear(refs.savings);
    if (product.compare_at_price && product.compare_at_price > product.price) {
      const saved = product.compare_at_price - product.price;
      const pct = Math.round((saved / product.compare_at_price) * 100);
      const dollarPill = elem('span', { className: 'qv-savings-pill', text: `You save ${formatMoney(saved)}` });
      const pctPill = elem('span', { className: 'qv-savings-pill qv-savings-pill--accent', text: `${pct}% off` });
      refs.savings.appendChild(dollarPill);
      refs.savings.appendChild(pctPill);
    }

    // ─── IMAGES (pre-sized slots, fade-in) ───────────────────────
    clear(refs.images);
    const images = (product.images || []).slice(0, 3);
    images.forEach((src, i) => {
      const isHero = i === 0;
      const slot = document.createElement('div');
      slot.className = 'qv-image-slot' + (isHero ? ' is-hero' : '');
      refs.images.appendChild(slot);
      const requested = buildShopifyImageUrl(src, isHero ? 900 : 450);
      attachImage(slot, requested, isHero);
    });

    // ─── DESCRIPTION (truncated, read-more) ──────────────────────
    clear(refs.description);
    const descText = stripHtml(product.description);
    if (descText) {
      const p = elem('p', { className: 'qv-description-text', text: descText });
      refs.description.appendChild(p);
      requestAnimationFrame(() => {
        if (p.scrollHeight > p.clientHeight + 4) {
          const toggle = elem('button', {
            className: 'qv-description-toggle',
            text: 'Read more',
            attrs: { type: 'button' },
          });
          toggle.addEventListener('click', () => {
            const expanded = p.classList.toggle('is-expanded');
            toggle.textContent = expanded ? 'Read less' : 'Read more';
          });
          refs.description.appendChild(toggle);
        }
      });
    }

    // ─── OPTION GROUPS (Size, Color, etc.) ───────────────────────
    clear(refs.optionGroups);
    selectedVariantId = null;

    const variants = product.variants || [];
    const options = product.options || [];
    const isSingleDefault =
      variants.length === 1 &&
      (variants[0].title === 'Default Title' || variants[0].option1 === 'Default Title');

    const firstAvailable = variants.find((v) => v.available) || variants[0];
    if (firstAvailable) selectedVariantId = firstAvailable.id;

    const selectedValues = firstAvailable
      ? [firstAvailable.option1, firstAvailable.option2, firstAvailable.option3]
      : [];

    function findVariantByOptions(opts) {
      return variants.find((v) =>
        opts.every((val, i) => val == null || v['option' + (i + 1)] === val)
      );
    }

    function updateForVariant(variant) {
      if (!variant) return;
      selectedVariantId = variant.available ? variant.id : null;
      clear(refs.price);
      refs.price.appendChild(document.createTextNode(formatMoney(variant.price)));
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const compare = elem('span', {
          className: 'qv-compare',
          text: ' ' + formatMoney(variant.compare_at_price),
        });
        refs.price.appendChild(compare);
      }
      renderMeta(refs.meta, product, variant);
      const addBtn = refs.ctaRow.querySelector('.qv-add');
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
    }

    if (!isSingleDefault && options && options.length) {
      const optionsMeta = (typeof options[0] === 'object')
        ? options
        : options.map((name, i) => ({ name, position: i + 1 }));

      optionsMeta.forEach((opt, optionIndex) => {
        const valueSet = Array.from(new Set(variants.map((v) => v['option' + (optionIndex + 1)]).filter(Boolean)));
        if (!valueSet.length) return;
        const group = elem('div', { className: 'qv-option-group' });
        const label = elem('p', { className: 'qv-option-label', text: opt.name || ('Option ' + (optionIndex + 1)) });
        const pillRow = elem('div', { className: 'qv-option-pills' });
        group.appendChild(label);
        group.appendChild(pillRow);

        valueSet.forEach((value) => {
          const btn = elem('button', {
            className: 'qv-option-pill',
            text: value,
            attrs: { type: 'button' },
          });
          if (selectedValues[optionIndex] === value) btn.classList.add('is-selected');
          btn.addEventListener('click', () => {
            selectedValues[optionIndex] = value;
            const variant = findVariantByOptions(selectedValues);
            if (variant) {
              [variant.option1, variant.option2, variant.option3].forEach((val, i) => {
                selectedValues[i] = val;
              });
              refs.optionGroups.querySelectorAll('.qv-option-group').forEach((g, gi) => {
                g.querySelectorAll('.qv-option-pill').forEach((p) => {
                  p.classList.toggle('is-selected', p.textContent === selectedValues[gi]);
                });
              });
              updateForVariant(variant);
            }
          });
          pillRow.appendChild(btn);
        });

        refs.optionGroups.appendChild(group);
      });
    }

    // ─── QUANTITY SELECTOR ───────────────────────────────────────
    clear(refs.qtyRow);
    const step = currentCaseSize > 1 ? currentCaseSize : 1;
    const minQty = Math.max(currentMinQty, step);
    let quantity = minQty;
    const qtyLabel = elem('span', { className: 'qv-qty-label', text: 'Quantity' });
    const qtyControl = elem('div', { className: 'qv-qty-control' });
    const qtyMinus = elem('button', { className: 'qv-qty-btn', text: '−', attrs: { type: 'button', 'aria-label': 'Decrease quantity' } });
    const qtyValue = elem('span', { className: 'qv-qty-value', text: String(quantity), attrs: { 'aria-live': 'polite' } });
    const qtyPlus = elem('button', { className: 'qv-qty-btn', text: '+', attrs: { type: 'button', 'aria-label': 'Increase quantity' } });
    qtyMinus.addEventListener('click', () => {
      if (quantity - step >= minQty) {
        quantity -= step;
        qtyValue.textContent = String(quantity);
      }
    });
    qtyPlus.addEventListener('click', () => {
      if (quantity + step <= 99) {
        quantity += step;
        qtyValue.textContent = String(quantity);
      }
    });
    qtyControl.appendChild(qtyMinus);
    qtyControl.appendChild(qtyValue);
    qtyControl.appendChild(qtyPlus);
    refs.qtyRow.appendChild(qtyLabel);
    refs.qtyRow.appendChild(qtyControl);

    // ─── META (SKU + stock) ──────────────────────────────────────
    renderMeta(refs.meta, product, firstAvailable);

    // ─── CTAs ────────────────────────────────────────────────────
    clear(refs.ctaRow);
    const addBtn = elem('button', {
      className: 'qv-add',
      text: firstAvailable && firstAvailable.available ? 'Add to cart' : 'Sold out',
      attrs: { type: 'button' },
    });
    if (firstAvailable && !firstAvailable.available) addBtn.disabled = true;
    addBtn.addEventListener('click', () => addToCart(quantity));
    const viewBtn = elem('a', {
      className: 'qv-view-full',
      text: 'View full details',
      attrs: { href: product.url || '#' },
    });
    refs.ctaRow.appendChild(addBtn);
    refs.ctaRow.appendChild(viewBtn);

    // ─── TRUST SIGNALS ───────────────────────────────────────────
    clear(refs.trust);
    // Threshold comes from the theme setting via the cart drawer's bar (0 = disabled).
    const fsEl = document.querySelector('[data-free-shipping-threshold]');
    const fsThreshold = fsEl ? parseInt(fsEl.dataset.freeShippingThreshold, 10) || 0 : 0;
    [
      fsThreshold > 0 ? { icon: '🚚', label: `Free shipping on orders over $${fsThreshold}` } : null,
      { icon: '↩', label: '30-day easy returns' },
      { icon: '✓', label: 'Authenticated by Goodbye Inventory' },
    ].filter(Boolean).forEach(({ icon, label }) => {
      const li = elem('li', { className: 'qv-trust-item' });
      const ico = elem('span', { className: 'qv-trust-icon', text: icon });
      const txt = elem('span', { className: 'qv-trust-text', text: label });
      li.appendChild(ico);
      li.appendChild(txt);
      refs.trust.appendChild(li);
    });
  }

  function onKeydown(e) {
    if (overlay && overlay.classList.contains('is-open') && e.key === 'Escape') {
      closeModal();
    }
  }

  function harvestHint(triggerEl) {
    if (!triggerEl) return null;
    // Look up the card the trigger lives in to grab the thumbnail and name
    const card = triggerEl.closest('.featured-products-editorial__card') || triggerEl.parentElement;
    if (!card) return null;
    const img = card.querySelector('img');
    return {
      previewSrc: img ? img.currentSrc || img.src : null,
      vendor: (card.querySelector('.featured-products-editorial__vendor') || {}).textContent || '',
      title: (card.querySelector('.featured-products-editorial__name') || {}).textContent || ''
    };
  }

  function openModal(handle, triggerEl) {
    lastTrigger = triggerEl;
    currentHandle = handle;
    selectedVariantId = null;
    var parsedMin = triggerEl ? parseInt(triggerEl.getAttribute('data-min-qty'), 10) : NaN;
    var parsedCase = triggerEl ? parseInt(triggerEl.getAttribute('data-case-size'), 10) : NaN;
    currentMinQty = Number.isFinite(parsedMin) && parsedMin > 0 ? parsedMin : 1;
    currentCaseSize = Number.isFinite(parsedCase) && parsedCase > 0 ? parsedCase : 1;
    ensureOverlay();
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    modalRefs.close.focus();

    // Open with a skeleton (and the trigger's thumbnail if available) so the
    // modal lays out at its final dimensions immediately. No more "small then
    // grow" jump when the high-res image lands.
    renderSkeleton(modalRefs, harvestHint(triggerEl));

    fetchProduct(handle)
      .then(renderProduct)
      .catch((err) => {
        console.error('[quick-view] fetch failed:', err);
        closeModal();
      });
  }

  function closeModal() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastTrigger) lastTrigger.focus();
  }

  function addToCart(quantity) {
    if (!selectedVariantId) return;
    const step = currentCaseSize > 1 ? currentCaseSize : 1;
    const minQty = Math.max(currentMinQty, step);
    let qty = parseInt(quantity, 10) || minQty;
    if (step > 1) qty = Math.round(qty / step) * step;
    if (qty < minQty) qty = minQty;

    const properties = {};
    const effectiveCaseSize = currentCaseSize > 1 ? currentCaseSize : currentMinQty;
    if (effectiveCaseSize > 1) properties._case_size = String(effectiveCaseSize);
    properties._min_qty = String(currentMinQty);
    const hasProps = Object.keys(properties).length > 0;

    const store = window.Alpine && window.Alpine.store ? window.Alpine.store('cart') : null;
    if (store && typeof store.add === 'function') {
      Promise.resolve(store.add(selectedVariantId, qty, hasProps ? properties : undefined)).then(closeModal);
    } else {
      const body = { id: selectedVariantId, quantity: qty };
      if (hasProps) body.properties = properties;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(closeModal);
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-quick-view-trigger]:not([data-qv-init])').forEach((btn) => {
      btn.setAttribute('data-qv-init', '');
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.dataset.productHandle;
        if (handle) openModal(handle, btn);
      });
      // Prefetch on hover/focus — by the time they click, the JSON is cached
      // and renderProduct runs in the same tick the modal opens.
      const prefetch = () => {
        const handle = btn.dataset.productHandle;
        if (handle) fetchProduct(handle).catch(() => {});
      };
      btn.addEventListener('pointerenter', prefetch, { once: true });
      btn.addEventListener('focus', prefetch, { once: true });
    });
  }

  document.addEventListener('keydown', onKeydown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.GBI = window.GBI || {};
  window.GBI.quickView = { init, open: openModal, close: closeModal };
})();
